import React from 'react';
import { useProCalculator } from '../hooks/useProCalculator';
import ProCalculatorFooter from './ProCalculatorFooter';
import SectionHelp from './SectionHelp';
import { Lightbulb } from 'lucide-react';
import '../styles/OrdersSection.css';

const OrdersSection = () => {
  const {
    orderRows,
    updateOrderRow,
    cookieRows,
    useSets,
    setRows,
    setSetRows, // The setter function for sets
    isMobile,
    // eslint-disable-next-line no-unused-vars -- 3.4.2026
    goToNextSection,
    activeSection,
    setActiveSection,
    loadedPlanName
  } = useProCalculator();

  return (
    <div className="orders-section">
      <div className="section-header">
        <h2 className="section-title">How Many Cookies</h2>
        {loadedPlanName && <span className="loaded-plan-name">Plan: {loadedPlanName}</span>}
      </div>
      {/* Help text container */}
      <div className="section-help-container">
        <SectionHelp sectionId="orders" isMobile={isMobile} />
      </div>

      {/* Pro tip disclaimer */}
      <div className="orders-tip-banner">
        <span className="orders-tip-icon"><Lightbulb size={18} /></span>
        <p>
          <strong>Pro Tip: Pad Your Numbers!</strong> To make sure you have enough icing, we recommend adding 1–2 extra cookies per shape. This gives you extra icing if you need to scrape and start over.
        </p>
      </div>
      
      {useSets ? (
        <div className="sets-orders-table">
          <div className="sets-orders-header">
            <div className="set-name-header">Set Name</div>
            <div className="count-header">Cookie Count</div>
          </div>
          
          {(() => {
            // Get unique set names
            const uniqueSets = [];
            const setNames = new Set();
            
            setRows.forEach(row => {
              if (row.setName && row.cookieId && !setNames.has(row.setName)) {
                setNames.add(row.setName);
                uniqueSets.push(row);
              }
            });
            
            return uniqueSets.map(row => (
              <div key={row.id} className="set-row">
                <div className="set-name-field">
                  {row.setName || 'Unnamed Set'}
                </div>
                <div className="count-field">
                  <input
                    type="number"
                    min="0"
                    max="999"
                    value={row.count || 0}
                    onChange={(e) => {
                      const value = Math.min(999, Math.max(0, parseInt(e.target.value) || 0));
                      
                      // Update count for all rows with this set name
                      const updatedRows = setRows.map(r => 
                        r.setName === row.setName ? { ...r, count: value } : r
                      );
                      setSetRows(updatedRows); // Use the correct setter function
                    }}
                    className="count-input"
                  />
                </div>
              </div>
            ));
          })()}
        </div>
      ) : (
        <div className="cookies-orders-table">
          <div className="cookies-orders-header">
            <div className="cookie-name-header">Cookie Name</div>
            <div className="count-header">Cookie Count</div>
          </div>
          
          {cookieRows.map(cookie => {
            // Find the corresponding order row
            const orderRow = orderRows.find(o => o.cookieId === cookie.id) || { count: 0 };
            
            return (
              <div key={cookie.id} className="cookie-order-row">
                <div className="cookie-name-field">
                  {cookie.cookieName && cookie.cookieName !== 'Cookie Name' ? cookie.cookieName : <em>Need Cookie Name</em>}
                </div>
                <div className="count-field">
                  <input
                    type="number"
                    min="0"
                    max="999"
                    value={orderRow.count || 0}
                    onChange={(e) => {
                      const value = Math.min(999, Math.max(0, parseInt(e.target.value) || 0));
                      
                      // updateOrderRow logic expects cookieId and the new count value
                      updateOrderRow(cookie.id, value); 
                    }}
                    onFocus={(e) => { const t = e.target; setTimeout(() => t.select(), 0); }}
                    className="count-input"
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {/* Footer with Save/Save As/Clear and Next buttons */}
      {activeSection !== 'all' && (
        <ProCalculatorFooter
          onBack={() => setActiveSection('colors')}
          backLabel="Back: Colors"
          onNext={() => setActiveSection('results')}
          nextLabel="Next: My Icing Plan"
        />
      )}
    </div>
  );
};

export default OrdersSection;
